import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import { GatsbyImage } from 'gatsby-plugin-image'
import { getShopifyImage } from 'gatsby-source-shopify'
import styled from '@emotion/styled'
import tw from 'twin.macro'
import { Link } from 'gatsby'
import { motion } from 'framer-motion'
import { StoreContext } from '../../contexts/ShopifyStoreContext'
import { formatPrice } from '../../utils/format-price'
import TrashBinIcon from '../../assets/trash-bin.svg'
import { GlobalDispatchContext } from '../../contexts/GlobalContextProvider'

const ItemWrap = styled((props) => <motion.div {...props} />)`
  ${tw`w-full flex items-center mb-4`}
`
const Thumbnail = styled.div`
  ${tw`bg-black mr-4 w-16 border hover:opacity-75`}
  height: 64px;
`

const MetaData = styled.div`
  ${tw`flex-1 mr-3`}
`

const TitleWrap = styled.div``

const Title = styled.h3`
  ${tw`uppercase font-extended text-lg font-medium tracking-wider`}
  ${tw`inline-block mr-2`}
`

const Quantity = styled.div`
  ${tw`font-mono text-sm`}
  ${tw`inline-block mr-1`}
`

const Description = styled.div`
  ${tw`font-mono text-sm mb-1`}
`

const PriceWrap = styled.div`
  ${tw`tracking-wider text-sm font-mono`}
  span {
    display: inline-block;
    margin-right: 0.5rem;
  }
  div {
    ${tw`inline-block opacity-50`}
    font-size: 0.65rem;
  }
`

const TaxNotice = styled.div`
  ${tw`p-1`}
`

const RemoveWrap = styled.div`
  ${tw`p-1`}
`

const RemoveButton = styled.button`
  ${tw`p-1 hover:opacity-80`}
`

const LineItem = ({ item }) => {
  const dispatch = useContext(GlobalDispatchContext)

  const { removeLineItem, checkout, updateLineItem, loading } = React.useContext(StoreContext)
  // const [quantity, setQuantity] = React.useState(item.quantity)

  const variantImage = {
    ...item.variant.image,
    originalSrc: item.variant.image.src,
  }
  const price = formatPrice(item.variant.priceV2.currencyCode, Number(item.variant.priceV2.amount))

  // const subtotal = formatPrice(
  //   item.variant.priceV2.currencyCode,
  //   Number(item.variant.priceV2.amount) * quantity
  // )

  const handleRemove = () => {
    removeLineItem(checkout.id, item.id)

    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        items: [
          {
            item_name: item?.title,
            item_id: item.variant?.sku,
            price: item.variant?.price,
            item_brand: 'Colekt',
            item_category: '',
            item_variant: item.variant?.title,
            quantity: 1,
          },
        ],
      },
    })
  }

  const uli = debounce((value) => updateLineItem(checkout.id, item.id, value), 300)
  // eslint-disable-next-line
  const debouncedUli = React.useCallback((value) => uli(value), [])

  const image = React.useMemo(
    () =>
      getShopifyImage({
        image: variantImage,
        layout: 'constrained',
        crop: 'contain',
        width: 160,
        height: 160,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variantImage.src]
  )

  return (
    <ItemWrap initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.15 }}>
      {image && (
        <Thumbnail>
          <Link
            to={`/product/${item.variant.product.handle}`}
            onClick={() => {
              dispatch({ type: 'TOGGLE_CART' })
            }}
          >
            <GatsbyImage key={variantImage.src} image={image} alt={variantImage.altText ?? item.variant.title} />
          </Link>
        </Thumbnail>
      )}
      <MetaData>
        <TitleWrap>
          <Link
            to={`/product/${item.variant.product.handle}`}
            onClick={() => {
              dispatch({ type: 'TOGGLE_CART' })
            }}
          >
            <Title>{item.title}</Title>{' '}
          </Link>
        </TitleWrap>
        <Description>
          {item.quantity > 1 && <Quantity>Qty:{item.quantity} |</Quantity>}
          {item.variant.title === 'Default Title' ? '' : item.variant.title}
        </Description>
        <PriceWrap>
          {price}
          <TaxNotice>incl. tax</TaxNotice>
        </PriceWrap>
      </MetaData>

      <RemoveWrap>
        <RemoveButton onClick={handleRemove} type="button" disabled={loading}>
          <TrashBinIcon />
        </RemoveButton>
      </RemoveWrap>
    </ItemWrap>
  )
}

LineItem.propTypes = {
  item: PropTypes.object.isRequired,
}

export default LineItem
